import React from 'react';
import {
  CheckCircle,
  Download,
  Home,
  Share,
  Smartphone,
  PlusSquare,
  type LucideIcon,
} from 'lucide-react';

export type LscInstallVisualPlatform = 'ios' | 'android' | 'generic';

export type LscInstallVisualStepsProps = {
  platform: LscInstallVisualPlatform;
  /** compact = banner hint row; full = modal / settings cards */
  density?: 'compact' | 'full';
  className?: string;
};

type VisualStep = {
  title: string;
  hint?: string;
  icon: LucideIcon;
};

const IOS_STEPS: VisualStep[] = [
  { title: 'Open in Safari', hint: 'Use Safari for the best install flow', icon: Smartphone },
  { title: 'Tap Share', hint: 'Share icon at the bottom of Safari', icon: Share },
  { title: 'Add to Home Screen', hint: 'Scroll the share menu if needed', icon: PlusSquare },
  { title: 'Confirm Stream Center', hint: 'Keep the name and tap Add', icon: CheckCircle },
];

const ANDROID_STEPS: VisualStep[] = [
  { title: 'Tap Install App', hint: 'Use the Install App button here', icon: Download },
  { title: 'Confirm install', hint: 'Approve the browser install prompt', icon: CheckCircle },
  { title: 'Open Stream Center', hint: 'Launch from your home screen or app drawer', icon: Home },
];

const GENERIC_STEPS: VisualStep[] = [
  { title: 'Add Stream Center', hint: 'Install or add to your home screen', icon: PlusSquare },
  { title: 'Open from your phone', hint: 'Launch like an app from your device', icon: Smartphone },
  { title: 'Jump back into your league', hint: 'Pick up live games, replays, and archives', icon: Home },
];

export function resolveInstallVisualPlatform(
  isIos: boolean,
  canPromptInstall: boolean
): LscInstallVisualPlatform {
  if (isIos && !canPromptInstall) return 'ios';
  if (canPromptInstall && !isIos) return 'android';
  if (isIos) return 'ios';
  if (canPromptInstall) return 'android';
  return 'generic';
}

function stepsForPlatform(platform: LscInstallVisualPlatform): VisualStep[] {
  switch (platform) {
    case 'ios':
      return IOS_STEPS;
    case 'android':
      return ANDROID_STEPS;
    default:
      return GENERIC_STEPS;
  }
}

function platformLabel(platform: LscInstallVisualPlatform): string {
  switch (platform) {
    case 'ios':
      return 'iPhone · Safari';
    case 'android':
      return 'Android · Chrome';
    default:
      return 'Any phone';
  }
}

function CompactStepPill({ step, index }: { step: VisualStep; index: number }) {
  const Icon = step.icon;
  return (
    <div
      className="flex min-w-0 flex-1 flex-col items-center gap-1 rounded-lg border border-primary-700/30 bg-secondary-900/50 px-2 py-2 text-center"
      data-testid={`lsc-install-visual-compact-step-${index + 1}`}
    >
      <span
        className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-[10px] font-bold text-white"
        aria-hidden
      >
        {index + 1}
      </span>
      <Icon className="h-3.5 w-3.5 text-primary-300" aria-hidden />
      <span className="text-[10px] font-medium leading-tight text-secondary-200 line-clamp-2">
        {step.title}
      </span>
    </div>
  );
}

function FullStepCard({ step, index }: { step: VisualStep; index: number }) {
  const Icon = step.icon;
  return (
    <li
      className="flex items-start gap-3 rounded-xl border border-secondary-700/80 bg-secondary-900/60 p-3"
      data-testid={`lsc-install-visual-step-${index + 1}`}
    >
      <div className="flex shrink-0 flex-col items-center gap-2">
        <div
          className="relative flex h-14 w-10 items-end justify-center rounded-[0.65rem] border-2 border-secondary-600 bg-gradient-to-b from-secondary-800 to-secondary-950 pb-1 shadow-inner"
          aria-hidden
        >
          <div className="absolute inset-x-1 top-1 h-1 rounded-full bg-secondary-700/80" />
          <Icon className="h-3.5 w-3.5 text-primary-300" />
        </div>
        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-[10px] font-bold text-white">
          {index + 1}
        </span>
      </div>
      <div className="min-w-0 flex-1 pt-1">
        <p className="text-sm font-medium text-white">{step.title}</p>
        {step.hint ? (
          <p className="mt-0.5 text-xs text-secondary-400">{step.hint}</p>
        ) : null}
      </div>
    </li>
  );
}

function StepGroup({
  platform,
  density,
  showHeading,
}: {
  platform: LscInstallVisualPlatform;
  density: 'compact' | 'full';
  showHeading: boolean;
}) {
  const steps = stepsForPlatform(platform);

  if (density === 'compact') {
    return (
      <div
        className="space-y-2"
        data-testid={`lsc-install-visual-steps-${platform}`}
        data-density="compact"
      >
        {showHeading ? (
          <p className="text-[11px] font-medium uppercase tracking-wide text-primary-300/90">
            {platformLabel(platform)}
          </p>
        ) : null}
        <div className="flex gap-2 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {steps.map((step, index) => (
            <CompactStepPill key={`${platform}-${step.title}`} step={step} index={index} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="space-y-2"
      data-testid={`lsc-install-visual-steps-${platform}`}
      data-density="full"
    >
      {showHeading ? (
        <h3 className="text-xs font-semibold uppercase tracking-wide text-primary-300/90">
          {platformLabel(platform)}
        </h3>
      ) : null}
      <ol className="space-y-2 list-none p-0 m-0" aria-label={`${platformLabel(platform)} install steps`}>
        {steps.map((step, index) => (
          <FullStepCard key={`${platform}-${step.title}`} step={step} index={index} />
        ))}
      </ol>
    </div>
  );
}

const LscInstallVisualSteps: React.FC<LscInstallVisualStepsProps> = ({
  platform,
  density = 'full',
  className = '',
}) => {
  if (platform === 'generic' && density === 'full') {
    return (
      <div
        className={`space-y-4 ${className}`.trim()}
        data-testid="lsc-install-visual-steps-generic-both"
      >
        <StepGroup platform="ios" density="full" showHeading />
        <StepGroup platform="android" density="full" showHeading />
      </div>
    );
  }

  return (
    <div className={className.trim()} data-testid="lsc-install-visual-steps">
      <StepGroup platform={platform} density={density} showHeading={density === 'full' && platform !== 'generic'} />
    </div>
  );
};

export default LscInstallVisualSteps;
